import React from "react";
import { Check, X, ArrowRight, Zap, DollarSign, Gauge, RefreshCw } from "lucide-react";
import Navbar from "../../components/Navbar";
const comparisonData = {
  qdrant: {
    name: "Qdrant",
    tagline: "Open-source vector search engine with advanced filtering",
    pros: [
      "Open-source with self-hosting flexibility",
      "Superior filtering and payload search capabilities",
      "Cost-effective for large-scale deployments",
      "Rich API with gRPC and REST support",
      "Excellent performance for filtered queries"
    ],
    cons: [
      "Requires infrastructure management expertise",
      "Smaller community compared to established players",
      "Limited enterprise tooling out of the box",
      "Cloud offering still developing features",
      "Steeper learning curve for beginners"
    ],
    pricing: {
      tier: "Budget-Friendly",
      description: "Free self-hosted. Cloud starts at $25/month. Pay only for compute resources.",
      note: "Best for: Cost-conscious teams with technical capabilities"
    },
    performance: {
      latency: "Sub-100ms for filtered queries",
      throughput: "Optimised for complex filtering"
    }
  },
  weaviate: {
    name: "Weaviate",
    tagline: "GraphQL-powered vector database with AI modules",
    pros: [
      "Native GraphQL API for intuitive querying",
      "Built-in AI modules (vectorisers, Q&A, etc.)",
      "Strong semantic search capabilities",
      "Flexible schema with automatic classification",
      "Active community and comprehensive documentation"
    ],
    cons: [
      "Higher resource consumption at scale",
      "Complex configuration for production",
      "GraphQL can be overkill for simple use cases",
      "Limited quantization options",
      "Pricing can escalate with managed service"
    ],
    pricing: {
      tier: "Moderate",
      description: "Free self-hosted. Cloud starts at $50/month with usage-based scaling.",
      note: "Best for: Teams leveraging AI modules and semantic search"
    },
    performance: {
      latency: "Sub-150ms average query time",
      throughput: "Good for semantic and hybrid search"
    }
  }
};

const migrationDetails = {
  difficulty: "Medium-Hard",
  timeEstimate: "3-8 hours for 1M vectors",
  considerations: [
    "GraphQL to REST/gRPC query translation needed",
    "Schema mapping from Weaviate classes to Qdrant collections",
    "Vectoriser configurations must be replicated",
    "Cross-reference relationships require restructuring"
  ],
  howWeHelp: [
    "Automated GraphQL to REST API conversion",
    "Schema transformation with class-to-collection mapping",
    "Vectoriser configuration migration assistance",
    "Zero-downtime migration with live sync",
    "Comprehensive validation and rollback support"
  ]
};

const ComparisonSection = ({ title, icon: Icon, children }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-8">
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center">
        <Icon className="w-5 h-5 text-black" />
      </div>
      <h2 className="text-2xl font-bold text-white">{title}</h2>
    </div>
    {children}
  </div>
);

const ProConList = ({ items, isPositive }) => (
  <ul className="space-y-3">
    {items.map((item, idx) => (
      <li key={idx} className="flex items-start gap-3">
        <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isPositive ? 'bg-white/20' : 'bg-gray-700'
        }`}>
          {isPositive ? (
            <Check className="w-3 h-3 text-green-500" />
          ) : (
            <X className="w-3 h-3 text-red-500" />
          )}
        </div>
        <span className="text-gray-300 text-sm">{item}</span>
      </li>
    ))}
  </ul>
);

const DatabaseCard = ({ data }) => (
  <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
    <h3 className="text-2xl font-bold text-white mb-2">{data.name}</h3>
    <p className="text-gray-400 text-sm mb-6">{data.tagline}</p>
    
    <div className="space-y-6">
      <div>
        <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
          <Check className="text-green-500 w-4 h-4" />
          Advantages
        </h4>
        <ProConList items={data.pros} isPositive={true} />
      </div>
      
      <div>
        <h4 className="text-gray-400 font-semibold mb-3 flex items-center gap-2">
          <X className="text-red-500 w-4 h-4" />
          Limitations
        </h4>
        <ProConList items={data.cons} isPositive={false} />
      </div>
    </div>
  </div>
);

const PricingCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-gray-300 border border-gray-700">
        {data.tier}
      </span>
    </div>
    <p className="text-gray-300 text-sm mb-3">{data.description}</p>
    <p className="text-gray-500 text-xs italic">{data.note}</p>
  </div>
);

const PerformanceCard = ({ data, name }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-xl font-bold text-white">{name}</h3>
    </div>
    <div className="space-y-2">
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Query Latency:</span>
        <span className="text-gray-300">{data.latency}</span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Throughput:</span>
        <span className="text-gray-300">{data.throughput}</span>
      </div>
    </div>
  </div>
);

export default function QdrantVsWeaviate() {
  return (
    <div className="bg-black min-h-screen px-6 py-10">
      <Navbar />
      <div className="max-w-6xl py-28 mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            Qdrant vs Weaviate:Comparison 2026
          </h1>
          <p className="text-gray-400 text-xl max-w-3xl mx-auto">
            In-depth comparison of two powerful open-source vector databases. Compare filtering 
            capabilities, AI modules, performance, and deployment flexibility.
          </p>
        </div>

        {/* Side-by-Side Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          <DatabaseCard data={comparisonData.qdrant} />
          <DatabaseCard data={comparisonData.weaviate} />
        </div>

        {/* Pricing Comparison */}
        <ComparisonSection title="Pricing Comparison" icon={DollarSign}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PricingCard data={comparisonData.qdrant.pricing} name="Qdrant" />
            <PricingCard data={comparisonData.weaviate.pricing} name="Weaviate" />
          </div>
          <div className="mt-8 p-6 bg-gray-800 border border-gray-700 rounded-lg">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Cost Analysis:</strong> Both offer free self-hosting. 
              Qdrant's cloud service is generally more cost-effective, while Weaviate's pricing 
              reflects its built-in AI modules and semantic search features. For 5M vectors, 
              Qdrant typically costs 30-40% less in cloud deployments.
            </p>
          </div>
        </ComparisonSection>

        {/* Performance Comparison */}
        <ComparisonSection title="Performance Metrics" icon={Gauge}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <PerformanceCard data={comparisonData.qdrant.performance} name="Qdrant" />
            <PerformanceCard data={comparisonData.weaviate.performance} name="Weaviate" />
          </div>
          <div className="mt-8 p-6 bg-gray-800 border border-gray-700 rounded-lg">
            <p className="text-gray-300 text-sm">
              <strong className="text-white">Performance Consideration:</strong> Qdrant excels in filtered 
              queries and complex payload searches with superior latency. Weaviate shines in semantic 
              search scenarios with its built-in AI modules, though it requires more resources at scale.
            </p>
          </div>
        </ComparisonSection>

        {/* Migration Difficulty */}
        <ComparisonSection title="Migration from Weaviate to Qdrant" icon={RefreshCw}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Migration Complexity</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Difficulty Level:</span>
                  <span className="text-gray-300 font-semibold">{migrationDetails.difficulty}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400 text-sm">Estimated Time:</span>
                  <span className="text-gray-300 text-sm">{migrationDetails.timeEstimate}</span>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-white mb-4">Key Considerations</h3>
              <ul className="space-y-2">
                {migrationDetails.considerations.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </ComparisonSection>

        {/* How Vector Migration Helps */}
        <div className="mt-16 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-12 text-center border border-gray-700">
          <div className="max-w-3xl mx-auto">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6">
              <Zap className="w-8 h-8 text-black" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">
              How Vector Migration Simplifies Your Switch
            </h2>
            <p className="text-gray-400 mb-8">
              We handle all the complexity of migrating from Weaviate to Qdrant, 
              including GraphQL conversion, schema mapping, and vectoriser configuration.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 text-left">
              {migrationDetails.howWeHelp.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 bg-black/40 rounded-lg p-4 border border-gray-800">
                  <Check className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
            
            {/* <button className="bg-white text-black px-8 py-4 rounded-lg font-semibold hover:bg-gray-200 transition-colors inline-flex items-center gap-2">
              Start Your Migration Today
              <ArrowRight className="w-5 h-5" />
            </button> */}
          </div>
        </div>

        {/* Final Recommendation */}
        <div className="mt-16 bg-gray-900 border border-gray-800 rounded-xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Which Should You Choose?</h2>
          <div className="space-y-4 text-gray-300">
            <p>
              <strong className="text-white">Choose Qdrant if:</strong> You need superior filtering 
              performance, want lower cloud costs, prefer gRPC/REST APIs, or have complex payload 
              search requirements. Ideal for cost-conscious teams with strong technical expertise.
            </p>
            <p>
              <strong className="text-white">Choose Weaviate if:</strong> You want built-in AI modules 
              (vectorisers, Q&A), prefer GraphQL APIs, need automatic classification, or require 
              extensive semantic search capabilities out of the box.
            </p>
            <p className="text-sm text-gray-500 italic">
              Both are excellent open-source options. Qdrant offers better performance-to-cost ratio, 
              while Weaviate provides more built-in AI features. Choose based on your team's expertise 
              and feature requirements.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}